import { useState } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBookBible } from "@fortawesome/free-solid-svg-icons";
import { useSermon, useAuth } from "../../util/hook";
import { TagBadgeStyle } from "../../style/SermonStyles";

const SermonSection = () => {
  const [activeTab, setActiveTab] = useState("all");
  const navigate = useNavigate();
  const { sermons: sermonData, loading, error } = useSermon();
  const { isLoggedIn } = useAuth();

  const filteredSermons =
    activeTab === "all"
      ? sermonData
      : sermonData.filter((sermon) => sermon.category === activeTab);

  const displayedSermons = filteredSermons.slice(0, 5);

  const handleTabClick = (tab: string) => {
    setActiveTab(tab);
  };

  const handleSermonClick = (sermonId: number) => {
    navigate(`/sermon/${sermonId}`, { state: { backTo: "/" } });
  };

  return (
    <Section className="sermon-section" id="sermon">
      <Container className="container">
        <SectionHeader className="section-header">
          <SectionTitle className="section-title">
            <FontAwesomeIcon icon={faBookBible} className="section-icon" /> 설교
          </SectionTitle>
          <SectionTitleEng className="section-title-eng">
            SERMON
          </SectionTitleEng>
          <SectionDesc className="section-desc">
            하나님의 말씀을 듣고, 믿고, 누리며 살아갑니다.
          </SectionDesc>
        </SectionHeader>

        <TabMenu className="tab-menu">
          <TabBtn
            className={`tab-btn ${activeTab === "all" ? "active" : ""}`}
            onClick={() => handleTabClick("all")}
          >
            전체
          </TabBtn>
          <TabBtn
            className={`tab-btn ${activeTab === "sunday" ? "active" : ""}`}
            onClick={() => handleTabClick("sunday")}
          >
            주일예배
          </TabBtn>
          <TabBtn
            className={`tab-btn ${activeTab === "friday" ? "active" : ""}`}
            onClick={() => handleTabClick("friday")}
          >
            금요모임
          </TabBtn>
          <TabBtn
            className={`tab-btn ${activeTab === "special" ? "active" : ""}`}
            onClick={() => handleTabClick("special")}
          >
            특별집회
          </TabBtn>
          <TabBtn
            className={`tab-btn ${activeTab === "seasonal" ? "active" : ""}`}
            onClick={() => handleTabClick("seasonal")}
          >
            절기
          </TabBtn>
        </TabMenu>

        {isLoggedIn && (
          <WriteButtonWrapper>
            <WriteButton
              onClick={() => navigate("/sermon/new", { replace: true })}
            >
              글쓰기
            </WriteButton>
          </WriteButtonWrapper>
        )}

        {loading && <LoadingMessage>설교를 불러오는 중...</LoadingMessage>}

        {error && (
          <ErrorMessage>설교를 불러오는 중 오류가 발생했습니다.</ErrorMessage>
        )}

        {!loading && !error && filteredSermons.length === 0 && (
          <EmptyMessage>
            등록된 설교가 없습니다.
            {isLoggedIn && (
              <EmptyActionButton
                onClick={() => navigate("/sermon/new", { replace: true })}
              >
                첫 설교 등록하기
              </EmptyActionButton>
            )}
          </EmptyMessage>
        )}

        {!loading && !error && filteredSermons.length > 0 && (
          <>
            <SermonTable className="sermon-table">
              <thead>
                <tr>
                  <th className="col-cate">구분</th>
                  <th className="col-title">설교제목</th>
                  <th className="col-scripture">본문</th>
                  <th className="col-date">날짜</th>
                  <th className="col-preacher">설교자</th>
                </tr>
              </thead>
              <tbody>
                {displayedSermons.map((sermon) => (
                  <SermonRow
                    key={sermon.id}
                    className="sermon-item"
                    onClick={() => handleSermonClick(sermon.id)}
                  >
                    <td>
                      <TagBadge className={`tag-badge tag-${sermon.category}`}>
                        {sermon.categoryName}
                      </TagBadge>
                    </td>
                    <td className="col-title">{sermon.title}</td>
                    <td className="col-scripture" style={{ textAlign: "left" }}>
                      {sermon.scripture.join(" / ")}
                    </td>
                    <td className="col-date">{sermon.date}</td>
                    <td>{sermon.preacher}</td>
                  </SermonRow>
                ))}
              </tbody>
            </SermonTable>

            <ViewAllButton onClick={() => navigate("/sermons")}>
              전체보기 ({filteredSermons.length}개)
            </ViewAllButton>
          </>
        )}
      </Container>
    </Section>
  );
};

const Section = styled.section`
  padding: 80px 0;
  background: #fff;
  position: relative;

  &:nth-child(even) {
    background: #f9f9f9;
  }

  @media (max-width: 768px) {
    padding: 60px 0;
  }

  @media (max-width: 480px) {
    padding: 40px 0;
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;

  @media (max-width: 768px) {
    padding: 0 15px;
  }

  @media (max-width: 480px) {
    padding: 0 10px;
  }
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 50px;
`;

const SectionTitle = styled.h2`
  font-size: 32px;
  color: #004094;
  margin-bottom: 5px;
  letter-spacing: -1px;
  line-height: 1.2;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  font-weight: 700;

  .section-icon {
    font-size: 0.9em;
    color: #004094;
  }

  @media (max-width: 768px) {
    font-size: 24px;
    flex-direction: column;
    text-align: center;
  }

  @media (max-width: 480px) {
    font-size: 20px;
    gap: 5px;
  }
`;

const SectionTitleEng = styled.span`
  font-size: 14px;
  color: #999;
  display: block;
  margin-bottom: 20px;
  letter-spacing: 2px;
  text-transform: uppercase;
  font-weight: 700;
`;

const SectionDesc = styled.p`
  font-size: 18px;
  color: #555;
  font-weight: 300;
  word-break: keep-all;
`;

const TabMenu = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 40px;
  border-bottom: 1px solid #ddd;

  @media (max-width: 768px) {
    justify-content: flex-start;
    overflow-x: auto;
    overflow-y: hidden;
    white-space: nowrap;
    padding: 0 10px;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;

    &::-webkit-scrollbar {
      display: none;
    }
  }

  @media (max-width: 480px) {
    padding: 0 15px;
  }
`;

const TabBtn = styled.div`
  padding: 15px 30px;
  font-size: 16px;
  color: #888;
  cursor: pointer;
  border-bottom: 3px solid transparent;
  transition: 0.3s;
  font-weight: 700;
  flex-shrink: 0;

  &:hover,
  &.active {
    color: #004094;
    border-bottom-color: #004094;
  }

  @media (max-width: 768px) {
    padding: 12px 20px;
    font-size: 14px;
    white-space: nowrap;
  }

  @media (max-width: 480px) {
    padding: 10px 15px;
    font-size: 12px;
    min-width: 70px;
    text-align: center;
  }
`;

const SermonTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  text-align: center;
  font-size: 15px;

  thead th {
    padding: 15px;
    background: #f4f4f4;
    color: #333;
    border-top: 2px solid #333;
    border-bottom: 1px solid #ccc;
    font-weight: 700;
  }

  tbody td {
    padding: 12px 15px;
    border-bottom: 1px solid #eee;
    color: #555;
    font-weight: 300;
  }

  tbody tr:hover {
    background: #f9fbfd;
  }

  .col-cate {
    width: 10%;
    color: #004094;
  }

  .col-title {
    width: 40%;
    text-align: left;
    padding-left: 30px !important;
    color: #222;
    cursor: pointer;
    font-weight: 700;
  }

  .col-scripture {
    width: 20%;
    color: #666;
    font-size: 14px;
  }

  .col-date {
    width: 15%;
    color: #888;
    font-size: 14px;
  }

  .col-preacher {
    width: 15%;
    font-weight: 700;
  }

  @media (max-width: 1024px) {
    display: block;
    overflow-x: auto;
    white-space: nowrap;
    font-size: 14px;
  }

  @media (max-width: 768px) {
    font-size: 12px;

    thead th {
      padding: 10px 8px;
    }

    tbody td {
      padding: 10px 8px;
    }

    .col-title {
      padding-left: 15px !important;
    }
  }
`;

const SermonRow = styled.tr`
  cursor: pointer;
  transition: 0.3s;

  &:hover {
    background: #f9f9f9;
  }

  &:hover .col-title {
    color: #004094;
  }
`;

const TagBadge = styled.span`
  ${TagBadgeStyle}
`;

const WriteButtonWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-bottom: 20px;
`;

const WriteButton = styled.button`
  padding: 10px 20px;
  background: #004094;
  color: #fff;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 700;

  &:hover {
    background: #003070;
  }
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: 60px 20px;
  font-size: 16px;
  color: #666;
`;

const ErrorMessage = styled.div`
  text-align: center;
  padding: 60px 20px;
  font-size: 16px;
  color: #c33;
  background-color: #fee;
  border-radius: 4px;
`;

const EmptyMessage = styled.div`
  text-align: center;
  padding: 60px 20px;
  font-size: 16px;
  color: #666;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
`;

const EmptyActionButton = styled.button`
  padding: 12px 24px;
  background: #004094;
  color: #fff;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 700;
  font-size: 14px;

  &:hover {
    background: #003070;
  }
`;

const ViewAllButton = styled.button`
  display: block;
  margin: 30px auto 0;
  padding: 12px 30px;
  background: #004094;
  color: #fff;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 700;
  font-size: 15px;

  &:hover {
    background: #003070;
  }
`;

export default SermonSection;
